/**
 * This component allows users to configure and test a Web API data source.
 * Users can:
 * - Enter API URL and HTTP method (GET/POST)
 * - Add query params, headers, and authentication
 * - Test API response before submitting
 * - Submit and navigate with fetched data
 */

import { useEffect, useState } from 'react';
import {
  AlertColor,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { fontSize, palette } from '../../..';
import { Helmet } from 'react-helmet-async';
import './WebApiDataSource.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BasicTabs from './Tabs';
import { AuthState, BodyState, HeaderParam, QueryParam } from './types';
import MenuBar from '../../DataViewer/MenuBar/MenuBar';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import RichTreeViewControl from '../../Controls/RichTreeViewControl';
import { getAllSubworkspace } from '../../../api/workspace';
import {
  ConvertListOfListToRichTreeViewList,
  findNodeWithParent,
  Node,
} from '../../CommonFunctions/CommonFunctions';
import { PopUpSpinner, SuccessFailureDialog } from '../../CommonFunctions/DialogComponents';
import useQuery from '../../../hooks/useQueryParams';
import { deleteContent, getContentDependencies } from '../../../api/workspace';
import { contentTypes } from '../../CommonFunctions/aliases';
import {
  getWebApiConnectionDetails,
  saveWebApiConnection,
  testWebApiConnection,
} from '../../../api/webApi';

const WebApiDataSource = () => {
  const { workspaceid } = useQuery('workspaceid');
  const { parentId, connectionId } = useParams();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState(!!connectionId);
  const [disableEdit, setDisableEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [workspaceList, setWorkspaceList] = useState<any[]>([]);
  const [notify, setNotify] = useState<{ message: string; severity: AlertColor } | null>(null);

  const [url, setUrl] = useState<string>('https://api.open-meteo.com/v1/forecast');
  const [method, setMethod] = useState('GET');
  const [params, setParams] = useState<QueryParam[]>([
    { name: 'latitude', value: '12.97', enabled: true },
    { name: 'longitude', value: '77.59', enabled: true },
    { name: 'current_weather', value: 'true', enabled: true },
  ]);
  const [body, setBody] = useState<BodyState>({
    type: 'json',
    raw: '{\n  "key": "value"\n}',
  });
  const [auth, setAuth] = useState<AuthState>({ type: 'none' });
  const [headers, setHeaders] = useState<HeaderParam[]>([
    { name: 'Content-Type', value: 'application/json', enabled: false },
    { name: 'Authorization', value: 'Bearer <token>', enabled: false },
  ]);
  const [connectionName, setConnectionName] = useState('Untitled Connection');

  const [openAlert, setOpenAlert] = useState(false);
  const [severity, setSeverity] = useState<AlertColor>('error');
  const [alertMessage, setAlertMessage] = useState('');

  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testData, setTestData] = useState<any>(null);

  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [dependencyData, setDependencyData] = useState<any[]>([]);
  const [showDependency, setShowDependency] = useState(false);

  // store previous schema (columns)
  const [previousSchema, setPreviousSchema] = useState<string[]>([]);

  const showAlert = (message: string, type: AlertColor = 'error') => {
    setAlertMessage(message);
    setSeverity(type);
    setOpenAlert(true);
    setTimeout(() => setOpenAlert(false), 4000);
  };

  useEffect(() => {
    const fetchConnectionDetails = async () => {
      if (!connectionId || !workspaceid) return;

      setLoading(true);
      const res = await getWebApiConnectionDetails(connectionId, workspaceid);

      if (!res.isError && res.data) {
        const data = res.data;
        const webAPIConfig =
          typeof data.webAPI === 'string' ? JSON.parse(data.webAPI) : data.webAPI;

        //  Set basic settings
        setUrl(webAPIConfig.url || '');
        setMethod(webAPIConfig.method || 'GET');
        setConnectionName(data.connectionName || '');

        // Map Params
        if (webAPIConfig.params) {
          setParams(
            Object.entries(webAPIConfig.params).map(([name, value]: any) => ({
              name,
              value: String(value),
              enabled: true,
            })),
          );
        } else {
          setParams([]);
        }

        // Map Headers
        if (webAPIConfig.headers) {
          setHeaders(
            Object.entries(webAPIConfig.headers)
              .map(([name, value]: any) => {
                const isAuthHeader = name.toLowerCase() === 'authorization';

                // Hide Authorization  if auth tab is used (to avoid duplication)
                if (isAuthHeader && webAPIConfig.auth?.type === 'bearer') {
                  return null; // skip adding header
                }

                return {
                  name,
                  value: isAuthHeader ? '******' : String(value),
                  enabled: true,
                };
              })
              .filter(Boolean) as HeaderParam[],
          );
        }

        // Map Body
        if (webAPIConfig.body && webAPIConfig.method !== 'GET') {
          const isObj = typeof webAPIConfig.body === 'object';
          setBody({
            type: isObj ? 'json' : 'text',
            raw: isObj ? JSON.stringify(webAPIConfig.body, null, 2) : webAPIConfig.body,
          });
        } else {
          setBody({
            type: 'none',
            raw: '',
          });
        }

        //  Map Auth
        if (webAPIConfig.auth) {
          const dbAuth = webAPIConfig.auth;

          if (dbAuth.type === 'basic') {
            setAuth({
              type: 'basic',
              basic: {
                username: '******',
                password: '******',
                enabled: true,
              },
            });
          } else if (dbAuth.type === 'bearer') {
            setAuth({
              type: 'bearer',
              bearer: {
                token: '******',
                prefix: dbAuth.prefix || 'Bearer',
                enabled: true,
              },
            });
          }
        } else {
          setAuth({ type: 'none' });
        }

        setDisableEdit(!data.privilege || data.privilege === 'view');
        setViewMode(true);

        // Capture initial schema
        const testRes = await testWebApiConnection(JSON.stringify(webAPIConfig));

        if (!testRes.isError) {
          const schema = extractSchema(testRes.data);
          setPreviousSchema(schema);
        }
      } else if (res.isError) {
        showAlert(res.message);
      }
      setLoading(false);
    };

    fetchConnectionDetails();
  }, [connectionId, workspaceid]);

  const handleEditClick = () => {
    const updatedAuth = { ...auth };
    if (updatedAuth.type === 'basic') {
      if (updatedAuth.basic?.username === '******') updatedAuth.basic.username = '';
      if (updatedAuth.basic?.password === '******') updatedAuth.basic.password = '';
    }
    if (updatedAuth.type === 'bearer' && updatedAuth.bearer?.token === '******') {
      updatedAuth.bearer.token = '';
    }
    setAuth(updatedAuth);

    const updatedHeaders = headers.map((h) => {
      if (h.name.toLowerCase() === 'authorization' && h.value === '******') {
        return { ...h, value: '' };
      }
      return h;
    });

    setHeaders(updatedHeaders);

    setViewMode(false);
  };

  const preparePayload = () => {
    const config: any = {
      url: url.trim(),
      method,
    };

    // params
    const filteredParams = params
      .filter((p) => p.enabled && p.name.trim() !== '')
      .reduce<Record<string, string>>((acc, p) => ({ ...acc, [p.name]: p.value }), {});

    if (Object.keys(filteredParams).length) config.params = filteredParams;

    // auth
    if (auth.type === 'basic' && auth.basic?.enabled) {
      config.auth = {
        type: 'basic',
        username: auth.basic.username,
        password: auth.basic.password,
      };
    } else if (auth.type === 'bearer' && auth.bearer?.enabled) {
      config.auth = {
        type: 'bearer',
        token: auth.bearer.token,
        prefix: auth.bearer.prefix || 'Bearer',
      };
    }

    // // headers
    const filteredHeaders = headers
      .filter((h) => h.enabled && h.name.trim() !== '')
      .reduce<Record<string, string>>((acc, h) => {
        const isAuthHeader = h.name.toLowerCase() === 'authorization';

        if (isAuthHeader && auth.type === 'bearer' && auth.bearer?.enabled) {
          return acc;
        }

        return { ...acc, [h.name]: h.value };
      }, {});

    if (Object.keys(filteredHeaders).length) config.headers = filteredHeaders;

    // body
    if (method === 'POST') {
      if (body.type === 'json' && body.raw.trim()) {
        const parsedBody = JSON.parse(body.raw);
        config.body = parsedBody;
      } else if (body.type === 'text' && body.raw.trim()) {
        config.body = body.raw;
      }
    }

    return JSON.stringify(config);
  };

  // Validate form inputs before testing or submitting
  const validateForm = (isTest = false) => {
    // Check URL
    if (!url.trim()) {
      showAlert('URL is required');
      return false;
    }
    try {
      new URL(url);
    } catch {
      showAlert('Please enter a valid URL');
      return false;
    }

    // Check Connection Name
    if (!isTest && !connectionName.trim()) {
      showAlert('Connection Name is required');
      return false;
    }

    // Check JSON Body
    if (method === 'POST' && body.type === 'json' && body.raw.trim()) {
      try {
        JSON.parse(body.raw);
      } catch (e) {
        showAlert('Invalid JSON format in Body tab');
        return false;
      }
    }
    // dont allow saving of masks
    if (auth.type === 'basic' && auth.basic?.enabled) {
      if (!auth.basic.username?.trim() || !auth.basic.password?.trim()) {
        showAlert('Username and Password are required for Basic Auth');
        return false;
      }
      if (auth.basic.username === '******' || auth.basic.password === '******') {
        showAlert('Please re-enter your credentials');
        return false;
      }
    }

    if (auth.type === 'bearer' && auth.bearer?.enabled) {
      if (!auth.bearer.token?.trim()) {
        showAlert('Token is required for Bearer Auth');
        return false;
      }
      if (auth.bearer.token === '******') {
        showAlert('Please re-enter your token');
        return false;
      }
    }

    const hasMaskedAuthHeader = headers.some(
      (h) => h.enabled && h.name.toLowerCase() === 'authorization' && h.value === '******',
    );

    if (hasMaskedAuthHeader) {
      showAlert('Please re-enter Authorization header');
      return false;
    }
    return true;
  };

  // Handle API Test - Fetch data and show in dialog
  const handleTest = async (showPopup = true) => {
    const sanitizedUrl = url.trim();
    setUrl(sanitizedUrl);

    if (!validateForm(true)) return false;

    const webAPIString = preparePayload();

    setLoading(true);

    const res = await testWebApiConnection(webAPIString);

    setLoading(false);

    if (!res.isError) {
      if (showPopup) {
        setTestData(res.data);
        setTestDialogOpen(true);
      }
      return true;
    } else {
      showAlert(res.message || 'Test Connection Failed.');
      return false;
    }
  };

  // Fetch workspaces for tree view selection
  const getWorkspaces = async () => {
    if (!validateForm()) return;

    const isConnectionValid = await handleTest(false);

    if (!isConnectionValid) return;

    try {
      if (workspaceList.length > 0) {
        setShowWorkspace(true);
        return;
      }
      const res = await getAllSubworkspace();
      if (res.isError) {
        setNotify({
          message: res.message,
          severity: 'error',
        });
      } else {
        setWorkspaceList(ConvertListOfListToRichTreeViewList(res.data!));
        setShowWorkspace(true);
      }
    } catch (error) {
      setNotify({
        message: 'Failed to fetch workspaces',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle API connection submission - Save connection and navigate

  const handleApiSubmit = async (_workspaceId: string, list: Node[], isUpdate = false) => {
    if (!validateForm()) return;

    const testRes = await handleTest(false);
    if (!testRes) return;

    const webAPIString = preparePayload();

    setLoading(true);

    const res = await saveWebApiConnection(
      _workspaceId,
      connectionName,
      webAPIString,
      isUpdate ? connectionId : undefined,
    );

    setLoading(false);

    if (!res.isError) {
      setShowWorkspace(false);
      if (isUpdate) {
        // message handled in handleUpdate
      } else {
        setNotify({ message: 'Connection saved successfully!', severity: 'success' });
      }

      if (isUpdate) {
        setViewMode(true);
        setTimeout(() => setNotify(null), 3000);
      }
      setTimeout(() => {
        const details = findNodeWithParent(list, _workspaceId);

        const navigationState = {
          state: {
            wname: details?.parentLabel || details?.label,
            childWorkspaceName: details?.label,
            parentId: details?.parentId,
          },
        };
        localStorage.setItem('workspace', details?.parentLabel || details?.label || '');
        localStorage.setItem('subworkspace', details?.label || '');

        if (details?.parentId) {
          navigate(`/SubWorkspaceDetails/${_workspaceId}`, navigationState);
        } else {
          navigate(`/workspace/${_workspaceId}?workspace=${details?.label}`, navigationState);
        }
      }, 2000);
    } else {
      setNotify({ message: res.data?.detail || 'Submit Failed', severity: 'error' });
    }
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);

      if (!validateForm()) return;

      const webAPIString = preparePayload();

      const testRes = await testWebApiConnection(webAPIString);

      if (testRes.isError) {
        showAlert('Test failed. Cannot update connection.');
        return;
      }

      const newSchema = extractSchema(testRes.data);

      const schemaChanged = isSchemaChanged(previousSchema, newSchema);

      const res = await getAllSubworkspace();
      if (res.isError) {
        setNotify({ message: res.message, severity: 'error' });
        return;
      }

      const list = ConvertListOfListToRichTreeViewList(res.data!);

      await handleApiSubmit(workspaceid!, list, true);
      if (schemaChanged) {
        setNotify({
          message:
            'Web API Connection updated successfully. However, you may need to update dependent datasets to reflect the changes.',
          severity: 'success',
        });
      } else {
        setNotify({
          message: 'Web API Connection updated successfully.',
          severity: 'success',
        });
      }

      setPreviousSchema(newSchema);
    } catch (error) {
      setNotify({ message: 'Failed to update connection', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Reset form to default values
  const handleReset = () => {
    setUrl('https://api.open-meteo.com/v1/forecast');
    setMethod('GET');
    setParams([
      { name: 'latitude', value: '12.97', enabled: true },
      { name: 'longitude', value: '77.59', enabled: true },
      { name: 'current_weather', value: 'true', enabled: true },
    ]);
    setBody({
      type: 'json',
      raw: '{\n  "key": "value"\n}',
    });
    setAuth({ type: 'none' });
    setHeaders([
      { name: 'Content-Type', value: 'application/json', enabled: false },
      { name: 'Authorization', value: 'Bearer <token>', enabled: false },
    ]);
  };

  const handleDelete = async () => {
    if (!connectionId || !workspaceid) return;
    setDeleteConfirmation(false);
    setLoading(true);
    try {
      // Check dependencies before deletion
      const depRes = await getContentDependencies(
        connectionId,
        contentTypes.DataConnection.toString(),
        workspaceid,
      );

      if (depRes.hasDependency) {
        setDependencyData(ConvertListOfListToRichTreeViewList(depRes.dependencies));
        setShowDependency(true);
        setLoading(false);
        return;
      }

      const res = await deleteContent(connectionId, workspaceid, contentTypes.DataConnection);
      if (res.isError) {
        setNotify({ message: res.message, severity: 'error' });
      } else {
        setNotify({ message: 'Connection deleted successfully', severity: 'success' });
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error) {
      setNotify({ message: 'Failed to delete connection', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatResponse = (data: any) => {
    try {
      let parsed = data;

      // handle nested stringified JSON
      while (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }

      return parsed;
    } catch {
      return data;
    }
  };

  const extractSchema = (data: any): string[] => {
    try {
      let parsed = data;

      while (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }

      if (Array.isArray(parsed) && parsed.length > 0) {
        return Object.keys(parsed[0]);
      }

      if (typeof parsed === 'object' && parsed !== null) {
        return Object.keys(parsed);
      }

      return [];
    } catch {
      return [];
    }
  };

  const isSchemaChanged = (oldSchema: string[], newSchema: string[]) => {
    if (oldSchema.length !== newSchema.length) return true;

    return !oldSchema.every((key) => newSchema.includes(key));
  };

  useEffect(() => {
    if (!connectionId) {
      if (method === 'GET') {
        setBody({
          type: 'none',
          raw: '',
        });
      } else if (method === 'POST') {
        setBody({
          type: 'json',
          raw: '{\n  "key": "value"\n}',
        });
      }
    }
  }, [method, connectionId]);

  // const disableEdit = false;
  return (
    <>
      <Helmet prioritizeSeoTags defer>
        <title>Web API Data Source | Silzila</title>
      </Helmet>

      {loading && <PopUpSpinner show={loading} />}

      {showDependency && (
        <RichTreeViewControl
          isWarning={true}
          list={dependencyData}
          showControls={false}
          showInPopup={true}
          handleCloseButtonClick={() => setShowDependency(false)}
          title={[`Can't delete this connection.`, 'It is being used by these datasets:']}
          hasMultipleTitle={true}
        />
      )}
      {showWorkspace && (
        <RichTreeViewControl
          currentWorkspace={parentId}
          proceedButtonName={'Save'}
          list={workspaceList}
          title={'Select a Workspace'}
          showInPopup={showWorkspace}
          handleCloseButtonClick={() => setShowWorkspace(false)}
          handleProceedButtonClick={(id: string, list: any) => {
            if (!id) {
              setNotify({ message: 'Select workspace', severity: 'error' });
              return;
            }
            handleApiSubmit(id, list);
          }}
        />
      )}

      {notify && (
        <SuccessFailureDialog
          show
          message={notify.message}
          severity={notify.severity}
          onClose={() => setNotify(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmation} onClose={() => setDeleteConfirmation(false)}>
        <DialogTitle sx={{ fontSize: fontSize.medium, fontWeight: 'bold' }}>
          Delete Web API Connection?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px' }}>
            This action cannot be undone. Are you sure you want to delete{' '}
            <strong>{connectionName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteConfirmation(false)}
            sx={{ color: 'gray', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <>
        <MenuBar from="fileUpload" disableEdit={disableEdit} />
        <div className="webapi-layout">
          {/* LEFT SIDEBAR */}
          <Box className="webapi-sidebar">
            <Typography
              className="webapi-sidebar-title"
              style={{
                fontSize: fontSize.large,
                color: palette.primary.contrastText,
                fontWeight: 'bold',
              }}
            >
              Add a Data Source
            </Typography>
            <Box className="webapi-options-container">
              <Box className="webapi-source-option selected">Web API</Box>
            </Box>
          </Box>
          {/* Main Content */}
          <div className="webapi-main" style={{ position: 'relative' }}>
            {/* BACK BUTTON */}
            <Box className="webapi-back" onClick={() => navigate(-1)}>
              <ArrowBackIcon style={{ color: '#b4b4b3' }} />
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: 'grey',
                }}
              >
                Back
              </Typography>
            </Box>
            {/* Connection Settings Section */}
            <Box className="webapi-content">
              <Box className="webapi-section">
                <Typography className="webapi-section-title" sx={{ fontSize: fontSize.large }}>
                  Connection settings
                </Typography>
                <Box className="webapi-field">
                  <Typography
                    className="webapi-label"
                    sx={{ fontSize: fontSize.medium, textAlign: 'center' }}
                  >
                    URL
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      select
                      disabled={viewMode}
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                      sx={{
                        width: '100px',
                        '& .MuiOutlinedInput-root': {
                          fontSize: fontSize.medium,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#2bbbad',
                          },
                        },
                        '& .MuiSelect-select': {
                          paddingY: '8.5px',
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                        },
                      }}
                      size="small"
                    >
                      <MenuItem value="GET" sx={{ fontSize: '12px' }}>
                        GET
                      </MenuItem>
                      <MenuItem value="POST" sx={{ fontSize: '12px' }}>
                        POST
                      </MenuItem>
                    </TextField>
                    <TextField
                      required
                      fullWidth
                      disabled={viewMode}
                      size="small"
                      id="outlined-basic"
                      variant="outlined"
                      placeholder="Example: https://api.open-meteo.com/v1/forecast"
                      value={url}
                      onChange={(e) => {
                        setUrl(e.target.value);
                      }}
                      onBlur={(e) => setUrl(e.target.value.trim())}
                    />
                  </Box>
                </Box>
                {/* tabs */}
                <BasicTabs
                  url={encodeURI(url)}
                  method={method}
                  params={params}
                  setParams={setParams}
                  body={body}
                  setBody={setBody}
                  auth={auth}
                  setAuth={setAuth}
                  header={headers}
                  setHeaders={setHeaders}
                  viewMode={viewMode}
                />
                <hr />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 3,
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  {viewMode ? (
                    <Button
                      variant="outlined"
                      onClick={() => setDeleteConfirmation(true)}
                      sx={{
                        textTransform: 'none',
                        color: '#6b7280',
                        borderColor: '#6b7280',
                        position: 'relative',
                        overflow: 'hidden',
                        zIndex: 1,

                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '0%',
                          height: '100%',
                          backgroundColor: '#d32f2f',
                          zIndex: -1,
                          transition: 'width 0.3s ease',
                        },

                        '&:hover::before': {
                          width: '100%',
                        },

                        '&:hover': {
                          color: '#fff',
                          borderColor: '#d32f2f',
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      DELETE
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => handleTest()}
                      sx={{
                        backgroundColor: '#e0e0e0',
                        color: '#444141',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#d5d5d5',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      TEST
                    </Button>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {viewMode ? (
                      <Button
                        variant="contained"
                        onClick={handleEditClick}
                        sx={{ backgroundColor: palette.primary.main, color: '#fff' }}
                      >
                        EDIT
                      </Button>
                    ) : (
                      <>
                        <TextField
                          size="small"
                          placeholder="Connection Name"
                          value={connectionName}
                          onChange={(e) => setConnectionName(e.target.value)}
                          sx={{ minWidth: '180px', flex: 1 }}
                        />

                        <Button
                          variant="outlined"
                          onClick={() => (connectionId ? setViewMode(true) : handleReset())}
                        >
                          {connectionId ? 'Cancel' : 'Reset'}
                        </Button>

                        <Button
                          variant="contained"
                          onClick={connectionId ? handleUpdate : getWorkspaces}
                          sx={{
                            backgroundColor: palette.primary.main,
                            color: '#fff',
                            textTransform: 'none',
                            '&:hover': {
                              backgroundColor: palette.primary.main,
                              boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
                            },
                          }}
                        >
                          {connectionId ? 'UPDATE' : 'SAVE'}
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </div>
        </div>
        <NotificationDialog
          openAlert={openAlert}
          severity={severity}
          testMessage={alertMessage}
          onCloseAlert={() => setOpenAlert(false)}
          sx={{
            '& .MuiDialog-container': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .MuiPaper-root': {
              margin: 'auto',
              position: 'relative',
              minWidth: '300px',
            },
          }}
        />
        {/* Test Result JSON Popup */}
        <Dialog
          open={testDialogOpen}
          onClose={() => setTestDialogOpen(false)}
          maxWidth="md"
          fullWidth
          aria-labelledby="test-result-title"
        >
          <DialogTitle id="test-result-title" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
            Test Result
          </DialogTitle>
          <DialogContent dividers>
            <Box
              component="pre"
              sx={{
                backgroundColor: '#f5f5f5',
                p: 2,
                borderRadius: '4px',
                overflow: 'auto',
                maxHeight: '400px',
                fontSize: '13px',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',

                '&::-webkit-scrollbar': {
                  width: '6px',
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#d1d1d1',
                  borderRadius: '10px',
                  '&:hover': {
                    backgroundColor: '#b4b4b3',
                  },
                },
                // Firefox support
                scrollbarWidth: 'thin',
                scrollbarColor: '#d1d1d1 transparent',
              }}
            >
              {testData ? JSON.stringify(formatResponse(testData), null, 2) : 'No data returned'}
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: '10px 24px' }}>
            <Button
              onClick={() => setTestDialogOpen(false)}
              variant="contained"
              size="small"
              sx={{
                textTransform: 'none',
                backgroundColor: palette.primary.main,
                color: '#fff',
                '&:hover': { backgroundColor: palette.primary.main },
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </>
    </>
  );
};

export default WebApiDataSource;
